import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../context-store";
import { SortDirection } from "../../../models/enums/SortDirectionEnum";
import PagedListModel from "../../../models/common/PagedListModel";
import modalUtils from "../../../utils/modal-utils";
import toasterUtils from "../../../utils/toaster-utils";
import { GeneralPoupConfig, ModalSize } from "../../../models/modal-types";
import { PagingEnum } from "../../../models/enums/IAIEnum";
import masterDataService from "../../../services/master-data-service";
import { GuidEnum } from "../../../models/enums/GuidEnum";
import { PageModeEnum } from "../../../models/enums/PageModeEnum";
import DomainModel from "../../../models/company/domain/response/DomainModel";
import DomainSearchInputModel from "../../../models/company/domain/request/DomainSearchInputModel";
import domainService from "../../../services/company/domain-service";
import DomainRequestModel from "../../../models/company/domain/request/DomainRequestModel";
import DomainCreateComponent from "./DomainCreateComponent";
import DomainGridComponent from "./DomainGridComponent";

const DomainComponent = () => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [domainPagedList, setDomainPagedList] = useState<PagedListModel<DomainModel>>();
    const [domainList, setDomainList] = useState<DomainModel[]>([]);
    const [refreshDomain, setRefreshDomain] = useState<number>(2);
    const [domainNameFilter, setDomainNameFilter] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("DomainName");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        loadPageSizes();
        getDomains(getSearchFilters(domainNameFilter, page, pageSize, sortColumn, sortDirection));
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (domainNameFilter: string, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: DomainSearchInputModel = {
            companyId: "",
            domainName: domainNameFilter,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getDomains = (inputData: DomainSearchInputModel) => {
        if (loggedInUser?.profileId) {
            inputData.companyId = loggedInUser?.profileId;
            modalUtils.showLoader();
            domainService.getDomains(inputData).then((res) => {
                modalUtils.closeModal();
                setDomainList([]);
                setDomainPagedList(res);
                setDomainList(res.items);
                setRefreshDomain(refreshDomain * 5);
            }).catch((error) => {
                modalUtils.closeModal();
                toasterUtils.showError(error);
            });
        }
    };

    const openDomainSaveModel = (domainRequestModel: DomainRequestModel, actionType: string) => {
        const config: GeneralPoupConfig = {
            title: "Add Domain",
            content: (
                <DomainCreateComponent
                    objDomain={domainRequestModel}
                    actionType={actionType}
                    onCloseDomain={onCloseDomainHandler}
                    onDomainSave={onDomainSaveHandler}
                />
            ),
            size: ModalSize.LG,
            // className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onCloseDomainHandler = () => {
        modalUtils.closeModal();
    };

    const onDomainSaveHandler = (objDomain: DomainRequestModel) => {
        modalUtils.showLoader();
        domainService.saveDomain(objDomain).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getDomains(getSearchFilters(domainNameFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onDomainActionHandler = (obj: DomainRequestModel, actionType: string) => {
        openDomainSaveModel(obj, actionType);
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getDomains(getSearchFilters(domainNameFilter, newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getDomains(getSearchFilters(domainNameFilter, PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        const newSortDirection = sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc;
        setSortDirection(newSortDirection)
        getDomains(getSearchFilters(domainNameFilter, page, pageSize, newSortColumn, newSortDirection));
    };

    const onFilterActionHandler = (filterColumn: string, filterValue: string) => {
        setPage(0);
        setDomainNameFilter(filterValue);
        getDomains(getSearchFilters(filterValue, 0, pageSize, sortColumn, sortDirection));
    };

    const addDomain = () => {
        if (loggedInUser !== null) {
            let domainRequestModel: DomainRequestModel = {
                domainId: 0,
                domainName: "",
                description: "",
                companyId: loggedInUser?.profileId,
                userId: loggedInUser?.userId
            }
            onDomainActionHandler(domainRequestModel, PageModeEnum.Create);
        }
    }

    return (
        <div className="card card-white">
            <div className="card-head card-head-text-right">
                <a className="btn-grid-add-action" title="Reload Data" onClick={() => getDomains(getSearchFilters(domainNameFilter, page, pageSize, sortColumn, sortDirection))}>
                    <i className="fa fa-refresh"></i>
                </a>
                <a className="btn-grid-add-action" onClick={addDomain}> <i className="fas fa-plus"></i> Add Domain</a>
            </div>
            <div className="card-body">
                {domainList.length > 0 && refreshDomain > 2 &&
                    <DomainGridComponent
                        key={refreshDomain}
                        domainList={domainList}
                        totalSize={domainPagedList?.totalCount ? domainPagedList?.totalCount : domainList.length}
                        currentPage={page + 1}
                        pagesize={pageSize}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                        pageSizesList={pageSizesList}
                        domainNameFilterValue={domainNameFilter}
                        onDomainAction={onDomainActionHandler}
                        onPageChangeAction={onPageChangeActionHandler}
                        onPageSizeChangeAction={onPageSizeChangeActionHandler}
                        onSortAction={onSortActionHandler}
                        onFilterAction={onFilterActionHandler} />
                }
            </div>
        </div>
    );
};
export default DomainComponent;